/**
 * Which rows the home page shows first, and in what order.
 *
 * Home walked the category groups in their static `sortOrder`, so Vehicles led
 * every home page for every visitor -- and Vehicles is 25,615 dealer-feed
 * listings to 539 of our own (prod, 2026-10-14). Now every row is scored, the
 * category rows, For You's special rows (trending, new arrivals, deals, near
 * you, budget, holiday, and the personal ones when the visitor has the history
 * for them) and the visitor's tag rows, and the page shows the highest first.
 *
 * score = base x (1 + HOME_ROW_AFFINITY_BOOST x affinity) x the page load's shuffle
 *
 * - A category row's base is its group's demand (summed engagement_score) with
 *   each listing weighted by the ranking's own source-tier weight, so a group
 *   made of click-out feeds sits where its own listings put it. Square-rooted
 *   and scaled to the top group, so Fashion's lead does not flatten the rest.
 * - Its affinity is the visitor's share of category views in the group (the
 *   ClickHouse preferences For You reads, and this session's own interest),
 *   scaled to the visitor's top group. A new visitor has none, and gets the
 *   base order: the fallback most of our visitors see.
 * - A special or tag row has a fixed prior on the same 0-1 scale. A personal
 *   row only exists when the visitor has the history it needs, so it is only
 *   ranked then.
 *
 * Pure. The server reads (utils/home-group-base.ts) and the page's merge
 * (buildHomeRows) feed it.
 */
import { resolveSourceTier, type RankingWeights } from './elasticsearch/relevance-weights';
import { shuffleFactor } from './seeded-shuffle';

/** Full affinity triples a row's score: a group the visitor shops lifts well above one they don't. */
export const HOME_ROW_AFFINITY_BOOST = 2;
/** +- share the page load's seed moves a row's score by, so near-equal rows trade places on reload. */
export const HOME_ROW_JITTER = 0.1;

/**
 * Priors for the rows that are not category groups, on the base's 0-1 scale
 * (the top group is 1; on prod the 4th is ~0.43 and Vehicles ~0.2).
 * Personal rows sit high: they exist only when the visitor has that history.
 */
export const HOME_SPECIAL_PRIORS = {
  similar_to_liked: 0.9,
  similar_to_purchased: 0.9,
  recently_viewed: 0.95,
  your_wishlist: 0.85,
  recent_searches: 0.8,
  holiday: 0.7,
  trending: 0.6,
  new_arrivals: 0.5,
  best_deals: 0.45,
  near_you: 0.42,
  budget: 0.3,
} as const;

export type HomeSpecialKind = keyof typeof HOME_SPECIAL_PRIORS;

/** The visitor's first tag row; each later one is HOME_TAG_ROW_STEP lower. */
export const HOME_TAG_ROW_PRIOR = 0.75;
export const HOME_TAG_ROW_STEP = 0.9;

export interface GroupSourceDemand {
  categoryGroupId: number;
  /** Per data source in the group: summed engagement_score. `null` is a listing with no source. */
  sources: Array<{ dataSourceId: number | null; demand: number }>;
}

/**
 * Each group's base, 0-1: tier-weighted demand, square-rooted, over the top
 * group's. A group with no demand is absent.
 */
export function groupBaseScores(
  groups: readonly GroupSourceDemand[],
  tierWeight: RankingWeights['sourceTierWeight'],
): Map<number, number> {
  const weighted = new Map<number, number>();
  for (const { categoryGroupId, sources } of groups) {
    const total = sources.reduce((sum, s) => sum + tierWeight[resolveSourceTier(s.dataSourceId)] * Math.max(0, s.demand), 0);
    if (total > 0) weighted.set(categoryGroupId, total);
  }
  const top = Math.max(0, ...weighted.values());
  if (top <= 0) return new Map();
  return new Map([...weighted].map(([id, w]) => [id, Math.sqrt(w / top)]));
}

/** Weights per group scaled to the largest, 0-1. Non-positive weights are dropped. */
export function scaleToTop(weights: Iterable<[number, number]>): Map<number, number> {
  const positive = [...weights].filter(([, w]) => w > 0);
  const top = Math.max(0, ...positive.map(([, w]) => w));
  if (top <= 0) return new Map();
  return new Map(positive.map(([id, w]) => [id, w / top]));
}

/**
 * The visitor's affinity per group, 0-1: the stronger of the ClickHouse view
 * history and this session's interest, each scaled to its own top group.
 */
export function groupAffinity(
  viewsByGroup: ReadonlyMap<number, number>,
  sessionInterest: ReadonlyArray<{ categoryGroupId: number; weight: number }>,
): Map<number, number> {
  const views = scaleToTop(viewsByGroup);
  const session = scaleToTop(sessionInterest.map(i => [i.categoryGroupId, i.weight] as [number, number]));
  const out = new Map(views);
  for (const [id, a] of session) out.set(id, Math.max(out.get(id) ?? 0, a));
  return out;
}

/** One row's score from its base (or prior) and its affinity. */
export function homeRowScore(base: number, affinity: number, shuffleId: number, seed: number | undefined): number {
  return base * (1 + HOME_ROW_AFFINITY_BOOST * Math.max(0, Math.min(1, affinity))) * shuffleFactor(shuffleId, seed, HOME_ROW_JITTER);
}

/**
 * The candidate groups, highest score first. Ties keep the order they came in
 * (the static order), so with no data at all the page is what it was.
 */
export function rankGroupOrder(
  candidates: readonly number[],
  base: ReadonlyMap<number, number>,
  affinity: ReadonlyMap<number, number>,
  seed: number | undefined,
): { order: number[]; scores: Record<number, number> } {
  const scored = candidates.map((id, index) => ({
    id,
    index,
    score: homeRowScore(base.get(id) ?? 0, affinity.get(id) ?? 0, id, seed),
  }));
  scored.sort((a, b) => b.score - a.score || a.index - b.index);
  return {
    order: scored.map(s => s.id),
    scores: Object.fromEntries(scored.map(s => [s.id, Number(s.score.toFixed(4))])),
  };
}

/** Rows that fetch their own listings: the page only places them. */
export type HomeSelfRow = 'budget' | 'near_you';

/** A row the page can place. `score` decides the first screen. */
export type HomeRow<G, S, T> =
  | { kind: 'group'; key: string; score: number; row: G }
  | { kind: 'special'; key: string; score: number; special: HomeSpecialKind; row: S }
  | { kind: 'self'; key: string; score: number; self: HomeSelfRow }
  | { kind: 'tag'; key: string; score: number; row: T };

export interface HomeRowsInput<G, S, T> {
  /** Group rows in the server's ranked order, every page so far, each with its score. */
  groupRows: ReadonlyArray<{ row: G; score: number; id: number }>;
  /** How many of `groupRows` came with the first page; later ones append below. */
  firstPageGroupCount: number;
  /** Special rows by kind; a missing one is left out. Arrays for the per-listing kinds. */
  specials: Partial<Record<Exclude<HomeSpecialKind, HomeSelfRow>, S | S[] | null>>;
  /** Rows that fetch their own listings, to be placed by their prior. */
  selfRows: readonly HomeSelfRow[];
  /** The visitor's tag rows, best first. Empty for a visitor with no history. */
  tagRows: readonly T[];
  tagOf: (row: T) => string;
  specialKeyOf?: (kind: HomeSpecialKind, row: S, index: number) => string;
  seed: number | undefined;
}

/**
 * The page order: the first page of group rows, the special rows and the tag
 * rows, highest score first; then later pages of group rows, appended so a row
 * already painted never moves.
 */
export function buildHomeRows<G, S, T>(input: HomeRowsInput<G, S, T>): Array<HomeRow<G, S, T>> {
  const first: Array<HomeRow<G, S, T>> = [];
  const cut = Math.max(0, Math.min(input.firstPageGroupCount, input.groupRows.length));

  for (const g of input.groupRows.slice(0, cut)) {
    first.push({ kind: 'group', key: `group-${g.id}`, score: g.score, row: g.row });
  }

  (Object.keys(input.specials) as Array<Exclude<HomeSpecialKind, HomeSelfRow>>).forEach((kind, kindIndex) => {
    const value = input.specials[kind];
    const rows = Array.isArray(value) ? value : value ? [value] : [];
    rows.forEach((row, i) => {
      // Two "Because you liked" rows share a prior; the second sits a step under the first.
      const prior = HOME_SPECIAL_PRIORS[kind] * Math.pow(HOME_TAG_ROW_STEP, i);
      first.push({
        kind: 'special',
        key: input.specialKeyOf ? input.specialKeyOf(kind, row, i) : `${kind}-${i}`,
        special: kind,
        score: homeRowScore(prior, 0, 1000 + kindIndex * 10 + i, input.seed),
        row,
      });
    });
  });

  input.selfRows.forEach((self, i) => {
    first.push({ kind: 'self', key: self, self, score: homeRowScore(HOME_SPECIAL_PRIORS[self], 0, 900 + i, input.seed) });
  });

  input.tagRows.forEach((row, i) => {
    first.push({
      kind: 'tag',
      key: `tag-${input.tagOf(row)}`,
      score: HOME_TAG_ROW_PRIOR * Math.pow(HOME_TAG_ROW_STEP, i),
      row,
    });
  });

  // Stable: equal scores keep the order they were pushed in.
  const ordered = first.map((r, i) => ({ r, i })).sort((a, b) => b.r.score - a.r.score || a.i - b.i).map(x => x.r);

  for (const g of input.groupRows.slice(cut)) {
    ordered.push({ kind: 'group', key: `group-${g.id}`, score: g.score, row: g.row });
  }
  return ordered;
}
